import React, { useEffect, useState } from 'react';
import SvgIcon, { SvgIconProps } from '@mui/material/SvgIcon';

interface SvgIconBaseProps extends SvgIconProps {
  src: string;
}

const ALLOWED_SVG_TAGS = new Set([
  'g', 'path', 'circle', 'rect', 'ellipse', 'line', 'polyline',
  'polygon', 'text', 'tspan', 'defs', 'use', 'symbol', 'clippath',
  'lineargradient', 'radialgradient', 'stop', 'mask', 'pattern',
  'marker', 'title', 'desc', 'image', 'filter', 'feblend',
  'fecolormatrix', 'fecomposite', 'feflood', 'fegaussianblur',
  'femerge', 'femergenode', 'feoffset',
]);

const ALLOWED_SVG_ATTRS = new Set([
  'd', 'fill', 'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin',
  'stroke-dasharray', 'stroke-dashoffset', 'opacity', 'fill-opacity',
  'stroke-opacity', 'cx', 'cy', 'r', 'rx', 'ry', 'x', 'y', 'x1', 'y1',
  'x2', 'y2', 'width', 'height', 'viewbox', 'transform', 'clip-path',
  'clip-rule', 'fill-rule', 'points', 'href', 'id', 'offset',
  'stop-color', 'stop-opacity', 'gradientunits', 'gradienttransform',
  'patternunits', 'patterntransform', 'patterncontentunits', 'markerwidth',
  'markerheight', 'refx', 'refy', 'orient', 'text-anchor', 'font-size',
  'font-family', 'font-weight', 'preserveaspectratio', 'dy', 'overflow',
  'style', 'aria-hidden', 'role', 'filter', 'mask', 'filterunits', 'maskunits',
  'flood-opacity', 'color-interpolation-filters', 'in', 'in2', 'result',
  'stddeviation', 'operator', 'mode', 'values', 'type', 'xlink:href',
  'k2', 'k3', 'enable-background',
]);

// Camel-case map for React SVG props
const ATTR_TO_REACT_PROP: Record<string, string> = {
  'stroke-width': 'strokeWidth',
  'stroke-linecap': 'strokeLinecap',
  'stroke-linejoin': 'strokeLinejoin',
  'stroke-dasharray': 'strokeDasharray',
  'stroke-dashoffset': 'strokeDashoffset',
  'fill-opacity': 'fillOpacity',
  'stroke-opacity': 'strokeOpacity',
  'clip-path': 'clipPath',
  'clip-rule': 'clipRule',
  'fill-rule': 'fillRule',
  'stop-color': 'stopColor',
  'stop-opacity': 'stopOpacity',
  'gradientunits': 'gradientUnits',
  'gradienttransform': 'gradientTransform',
  'patternunits': 'patternUnits',
  'patterntransform': 'patternTransform',
  'markerwidth': 'markerWidth',
  'markerheight': 'markerHeight',
  'refx': 'refX',
  'refy': 'refY',
  'text-anchor': 'textAnchor',
  'font-size': 'fontSize',
  'font-family': 'fontFamily',
  'font-weight': 'fontWeight',
  'preserveaspectratio': 'preserveAspectRatio',
  'viewbox': 'viewBox',
  'xlink:href': 'xlinkHref',
  'flood-opacity': 'floodOpacity',
  'color-interpolation-filters': 'colorInterpolationFilters',
  'filterunits': 'filterUnits',
  'maskunits': 'maskUnits',
  'patterncontentunits': 'patternContentUnits',
  'stddeviation': 'stdDeviation',
  'aria-hidden': 'aria-hidden',
  'enable-background': 'enableBackground',
};

let _keyCounter = 0;

function domNodeToReact(node: Element, depth = 0): React.ReactElement | null {
  const tag = node.tagName.toLowerCase();
  if (!ALLOWED_SVG_TAGS.has(tag)) return null;

  const props: Record<string, string | number> = { key: `${tag}-${depth}-${_keyCounter++}` };

  for (const attr of Array.from(node.attributes)) {
    const name = attr.name.toLowerCase();
    if (ALLOWED_SVG_ATTRS.has(name)) {
      const reactProp = ATTR_TO_REACT_PROP[name] ?? name;
      props[reactProp] = attr.value;
    }
  }

  const children: React.ReactElement[] = [];
  for (const child of Array.from(node.children)) {
    const el = domNodeToReact(child, depth + 1);
    if (el) children.push(el);
  }

  return React.createElement(tag, props, children.length > 0 ? children : undefined);
}

function parseSvg(text: string): { viewBox: string; elements: React.ReactElement[] } {
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, 'image/svg+xml');

  if (doc.querySelector('parsererror')) {
    return { viewBox: '0 0 24 24', elements: [] };
  }

  const svgEl = doc.querySelector('svg');
  if (!svgEl) return { viewBox: '0 0 24 24', elements: [] };

  const viewBox = svgEl.getAttribute('viewBox') ?? '0 0 24 24';
  _keyCounter = 0;
  const elements: React.ReactElement[] = [];
  for (const child of Array.from(svgEl.children)) {
    const el = domNodeToReact(child);
    if (el) elements.push(el);
  }

  return { viewBox, elements };
}

const SvgIconBase: React.FC<SvgIconBaseProps> = ({ src, ...props }) => {
  const [viewBox, setViewBox] = useState<string>('0 0 24 24');
  const [elements, setElements] = useState<React.ReactElement[]>([]);

  useEffect(() => {
    let cancelled = false;

    fetch(src)
      .then((res) => {
        if (!res.ok) throw new Error('SVG fetch failed');
        return res.text();
      })
      .then((text) => {
        if (cancelled) return;
        const { viewBox: vb, elements: els } = parseSvg(text);
        setViewBox(vb);
        setElements(els);
      })
      .catch(() => {
        if (!cancelled) setElements([]);
      });

    return () => {
      cancelled = true;
    };
  }, [src]);

  return (
    <SvgIcon viewBox={viewBox} {...props}>
      {elements}
    </SvgIcon>
  );
};

export default SvgIconBase;
